import { motion } from 'framer-motion';
import { type FC, useEffect, useState } from 'react';
import { GlobalLoader } from '@/app/components/shared/GlobalLoader';
import { cardItemVariants, cardsContainerVariants } from '@/app/components/shared/motion-variants';
import { trackClickButton } from '@/app/components/shared/TikTokPixel';
import { SITE } from '@/app/constants';
import { PAID_TRIAL_PERIOD, useGotoManageStripe } from '@/app/hooks';
import type {
  PaidTrialInfo,
  PeriodType,
  ProductList,
  ProductPlan,
  SubscriptionProductType,
  TabItemType,
} from '@/app/types';
import { cn } from '@/app/utils';
import { ProductEmptyState } from '../ProductEmptyState';
import { EditionSwitcher, type PlanEdition } from './components/EditionSwitcher';
import { ErrorComponent } from './components/Error';
import { PlanCard } from './components/PlanCard';
import { SubscriptionButton } from './components/SubscriptionButton';
import { Tab } from './components/Tab';

export const PlanDetail: FC<{
  source?: string;
  period?: PeriodType | null;
  setPeriod?: (period: PeriodType | null) => void;
  loadingProducts: boolean;
  products: ProductList | null;
  tabList: TabItemType[];
  error?: Error | null;
  editionOptions?: { key: PlanEdition; label: string }[];
  edition?: PlanEdition;
  onEditionChange?: (key: PlanEdition) => void;
  /** 弹窗来源：套餐弹窗打开时的 from_page，透传给订阅按钮以随 vip_price_click 上报 modal_from_page */
  modalFromPage?: string;
  /** 试用价格信息：横幅由订阅页统一渲染，这里仅用于卡片价格展示 */
  paidTrialInfo?: PaidTrialInfo | null;
}> = ({
  source,
  period: externalPeriod,
  setPeriod: externalSetPeriod,
  loadingProducts,
  products,
  tabList,
  error,
  editionOptions,
  edition,
  onEditionChange,
  modalFromPage,
  paidTrialInfo,
}) => {
  const { loading, handleGotoManageStripe } = useGotoManageStripe();

  const [internalPeriod, setInternalPeriod] = useState<PeriodType | null>(null);
  const setPeriod = externalSetPeriod ?? setInternalPeriod;
  const period = internalPeriod ?? externalPeriod;

  useEffect(() => {
    if (!products || loadingProducts || !externalPeriod) return;
    SITE === 'global' && trackClickButton();
  }, [products, loadingProducts, externalPeriod]);

  const renderSubscriptionButton = (product: SubscriptionProductType) => {
    return (
      <SubscriptionButton
        source={source || ''}
        loading={loading}
        product={product}
        getManagement={handleGotoManageStripe}
        modalFromPage={modalFromPage}
        isPaidTrial={isPaidTrial}
      />
    );
  };

  /**
   * 计算"年付 vs 月付"节省金额（cent），用于卡片订阅按钮下方的提示。
   * 仅 yearly 周期且找得到同 plan 的 monthly 产品才返回正数。
   */
  const getMonthlyEquivalentSaved = (product: SubscriptionProductType): number => {
    if (product.billing_period !== 'yearly') return 0;
    const monthlyPeriod: PeriodType = product.product_type === 'subscription' ? 'subscription_monthly' : 'pass_monthly';
    const monthly = products?.[monthlyPeriod]?.[product.plan_type as keyof ProductPlan];
    if (!monthly) return 0;
    const saved = 12 * (monthly.price ?? 0) - (product.price ?? 0);
    return saved > 0 ? saved : 0;
  };

  if (error) return <ErrorComponent />;
  if (loadingProducts && !products)
    return (
      <div className="flex min-h-[400px] w-full items-center justify-center">
        <GlobalLoader className="static translate-x-0 translate-y-0" />
      </div>
    );
  if (!products || !period || tabList.length === 0) return <ProductEmptyState className="mt-10" />;

  // period 可由 URL 指定（开屏弹窗带 subscription-period=paid_trial 直达试用 tab）。
  // 若该 period 不在当前用户的 tabList 里（后端未下发试用档），回落到第一个可用 tab：
  // 否则 tab 条会没有选中态，卡片还会按试用口径渲染积分并给下单带上 use_paid_trial。
  const activePeriod = tabList.some((item) => item.value === period) ? period : (tabList[0]?.value ?? period);
  const isPaidTrial = activePeriod === PAID_TRIAL_PERIOD;
  // Trial tab re-uses the subscription_monthly product set (same plans, different price rendering)
  const effectivePeriod = isPaidTrial ? ('subscription_monthly' as typeof period) : activePeriod;

  return (
    <>
      <div className="select-none text-center">
        {tabList.length > 0 && activePeriod && (
          <div className="relative sm:flex sm:flex-col">
            <Tab setPeriod={setPeriod} period={activePeriod} tabList={tabList} />
            <EditionSwitcher
              editionOptions={editionOptions}
              edition={edition}
              onEditionChange={onEditionChange}
              className="-translate-y-1/2 absolute top-1/2 right-0 sm:static sm:mr-4 sm:ml-auto sm:w-fit sm:translate-y-0"
            />
          </div>
        )}
      </div>

      <motion.div
        key={activePeriod}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        className={cn(
          'grid gap-4 sm:mt-0 sm:grid-cols-1 sm:px-4',
          // 弹窗场景隐藏免费版后仅 3 张卡片，用 3 列避免留空列
          source === 'dialog' ? 'grid-cols-3 md:grid-cols-2' : 'grid-cols-4 lg:grid-cols-2',
        )}
        initial="hidden"
        animate="visible"
        variants={cardsContainerVariants}
      >
        {Object.keys(products[effectivePeriod] || {}).map((key) => {
          const product = products[effectivePeriod]?.[key as keyof ProductPlan];
          if (!product) return null;
          // 弹窗场景不展示免费版卡片
          if (source === 'dialog' && key === 'free') return null;
          const isDiscount = tabList.find((v) => v.value === activePeriod)?.isDiscount;
          return (
            <motion.div key={`${key}_${activePeriod}`} variants={cardItemVariants}>
              <PlanCard
                isDiscount={isDiscount}
                product={product}
                renderSubscriptionButton={renderSubscriptionButton}
                monthlyEquivalentSaved={getMonthlyEquivalentSaved(product)}
                isPaidTrial={isPaidTrial}
                paidTrialInfo={isPaidTrial ? (paidTrialInfo ?? undefined) : undefined}
              />
            </motion.div>
          );
        })}
      </motion.div>
    </>
  );
};
